import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * PriceField, the currency-labelled numeric input.
 *
 * CSS selector: `mobile/molecules.css` `.ds-price-field`, with
 * `.ds-price-field__currency` and `.ds-price-field__label`.
 *
 * Doctrine: prices are South African Rands, so the currency glyph defaults to
 * `R` and is never a dollar sign. This is deliberately a bespoke control rather
 * than a wrapped `<Input>`, because its anatomy of a leading currency glyph and
 * a trailing micro-label sharing one bordered row does not fit the input's own
 * padding and border model.
 */
export const PriceField = React.forwardRef(function PriceField(
  { className, currency = 'R', fieldLabel, inputMode = 'numeric', ...rest },
  ref,
) {
  return (
    <div className={cn('ds-price-field', className)}>
      <span className="ds-price-field__currency">{currency}</span>
      <input ref={ref} inputMode={inputMode} {...rest} />
      {fieldLabel ? <span className="ds-price-field__label">{fieldLabel}</span> : null}
    </div>
  );
});

/**
 * PriceInputs, the min to max range layout that holds two price fields.
 * CSS selector: `mobile/molecules.css` `.ds-price-inputs`.
 */
export const PriceInputs = React.forwardRef(function PriceInputs({ className, ...rest }, ref) {
  return <div ref={ref} className={cn('ds-price-inputs', className)} {...rest} />;
});

/**
 * PriceInputsDash, the muted range separator between the two price fields.
 * CSS selector: `mobile/molecules.css` `.ds-price-inputs__dash`. It is an en
 * dash, the range separator, and it is hidden from assistive tech because the
 * two fields carry their own labels.
 */
export function PriceInputsDash({ className }) {
  return (
    <span className={cn('ds-price-inputs__dash', className)} aria-hidden="true">
      &ndash;
    </span>
  );
}

export default PriceField;
